import { useState, useMemo, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TabEnum, TabType } from "../../Enums/EventEnums";
import "./Events.css";
import StaffRegistrationTab from "./components/StaffRegistrationTab";
import StaffAccomodationsTab from "./components/StaffAccomodationsTab";
import StaffSpecialEventTab from "./components/StaffSpecialEventTab";
import StaffOverviewTab from "./components/StaffOverviewTab";
import StaffPhotoGalleryTab from "./components/StaffPhotoGalleryTab";
import StaffAttendeesTab from "./components/StaffAttendeesTab";
import StaffContactUsTab from "./components/StaffContactUsTab";
import Pencil from "../../components/Atomos/Icons/ViewMode/Pencil";
import SaveChanges from "../../components/Atomos/Icons/EditMode/SaveChanges";
import Eye from "../../components/Atomos/Icons/ViewMode/Eye";
import { useNavigate, useParams } from "react-router-dom";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import chevronRightButton from "../../assets/Fonts/chevron-right-button.svg";
import StaffProgramTab from "./components/StaffProgamTab";
import ChevronDown from "../../components/Atomos/Icons/ChevronDown";
import { useTabEditMode } from "../../hooks/Events/useTabEditMode";
import { useEventLoader } from "../../hooks/Events/useEventLoader";
import { setStaffSelectedTab } from "../../redux/states/staffEventSelectedTab.state";
import { Appstore } from "../../redux/store";
import { NewEvent } from "../../redux/states/event.state";
import SwitchToggle from "../../components/Atomos/Switch-Toogle/Switch";
import { updateEvent, getEventById } from "../../Services/Event";
import { toast } from "react-toastify";

interface TabEditButtonConfig {
  enabled: boolean;
  editText: string;
  previewText?: string;
  showIcon: boolean;
  customAction?: (isEditMode: boolean) => void;
}

interface TabEditButtonProps {
  config: TabEditButtonConfig;
  isEditMode: boolean;
  onToggle: () => void;
  eventTitle?: string;
}

function TabEditButton({
  config,
  isEditMode,
  onToggle,
  eventTitle,
}: TabEditButtonProps) {
  if (!config.enabled) {
    return null;
  }

  const buttonText =
    isEditMode && config.previewText ? config.previewText : config.editText;

  const handleClick = () => {
    if (config.customAction) {
      config.customAction(isEditMode);
    } else {
      onToggle();
    }
  };

  return (
    <div className="events-content-container-inner">
      {eventTitle && <p>{eventTitle.toUpperCase()}</p>}
      <button
        onClick={handleClick}
        className="flex items-center gap-2 text-white"
      >
        <p>{buttonText}</p>
        {config.showIcon && (
          <div className="save-icon-white">
            {buttonText === "PREVIEW SESSION" ? (
              <Eye />
            ) : isEditMode ? (
              <SaveChanges />
            ) : (
              <Pencil />
            )}
          </div>
        )}
      </button>
    </div>
  );
}

const tabEditConfig: Record<TabType, TabEditButtonConfig> = {
  accomodation: {
    enabled: true,
    editText: "EDIT",
    previewText: "PREVIEW",
    showIcon: true,
  },
  registration: {
    enabled: true,
    editText: "EDIT",
    previewText: "PREVIEW",
    showIcon: true,
  },
  specialEvent: {
    enabled: true,
    editText: "EDIT",
    previewText: "PREVIEW",
    showIcon: true,
  },
  overview: {
    enabled: true,
    editText: "EDIT",
    previewText: "PREVIEW",
    showIcon: true,
  },
  gallery: {
    enabled: true,
    editText: "EDIT",
    previewText: "PREVIEW",
    showIcon: true,
  },
  attendees: {
    enabled: true,
    editText: "EDIT",
    previewText: "PREVIEW",
    showIcon: true,
  },
  contactUs: {
    enabled: true,
    editText: "EDIT",
    previewText: "PREVIEW",
    showIcon: true,
  },
  program: {
    enabled: true,
    editText: "NEW SESSION",
    previewText: "PREVIEW SESSION",
    showIcon: true,
  },
};

export default function StaffEventDetails() {
  const dispatch = useDispatch();
  const activeTab = useSelector((state: Appstore) => state.staffEventSelectedTab.selectedTab);
  const reduxEvent = useSelector((state: Appstore) => state.event);
  const [isAccommodationsEditMode, setIsAccommodationsEditMode] = useState(false);
  const [isRegistrationEditMode, setIsRegistrationEditMode] = useState(false);
  const [isOverviewEditMode, setIsOverviewEditMode] = useState(false);
  const [isProgramEditMode, setIsProgramEditMode] = useState(false);
  const [isSpecialEventEditMode, setIsSpecialEventEditMode] = useState(false);
  const [isGalleryEditMode, setIsGalleryEditMode] = useState(false);
  const [isAttendeesEditMode, setIsAttendeesEditMode] = useState(false);
  const [isContactUsEditMode, setIsContactUsEditMode] = useState(false);
  const [comingSoonToggleByTab, setComingSoonToggleByTab] = useState<Record<TabType, boolean>>({
    registration: false,
    accomodation: false,
    specialEvent: false,
    overview: false,
    gallery: false,
    attendees: false,
    contactUs: false,
    program: false,
  });
  const { id } = useParams();
  const { isEditMode, toggleEditMode } = useTabEditMode();
  const navigate = useNavigate();

  const event = useEventLoader(id);

  const getTabType = useCallback((
    tab: TabType | { type: TabType; index: number }
  ): TabType => {
    return typeof tab === "string" ? tab : tab.type;
  }, []);

  const isTabObject = useCallback((
    tab: TabType | { type: TabType; index: number }
  ): tab is { type: TabType; index: number } => {
    return typeof tab === "object" && tab !== null && "index" in tab;
  }, []);

  useEffect(() => {
    if (event?.tabs) {
      const initialToggleState: Record<TabType, boolean> = {
        registration: false,
        accomodation: false,
        specialEvent: false,
        overview: false,
        gallery: false,
        attendees: false,
        contactUs: false,
        program: false,
      };

      event.tabs.forEach((tab) => {
        const tabType = getTabType(tab as TabType | { type: TabType; index: number });
        if (tabType && typeof tab === 'object' && tab !== null && 'comingSoon' in tab) {
          initialToggleState[tabType] = (tab as any).comingSoon ?? false;
        }
      });

      setComingSoonToggleByTab(initialToggleState);
    }
  }, [event?.tabs, getTabType]);
  
  const handleAccommodationsEditClick = useCallback(() => {
    setIsAccommodationsEditMode((prev) => !prev);
  }, []);

  const handleRegistrationEditClick = useCallback(() => {
    setIsRegistrationEditMode((prev) => !prev);
  }, []);

  const handleOverviewEditClick = useCallback(() => {
    setIsOverviewEditMode((prev) => !prev);
  }, []);

  const handleComingSoonToggleChange = useCallback(async (activeTabType: TabType, newValue: boolean) => {
    if (!event?.id) {
      return;
    }

    setComingSoonToggleByTab((prev) => ({
      ...prev,
      [activeTabType]: newValue,
    }));

    try {
      const existingTabs = event.tabs || [];
      const tabExists = existingTabs.some((tab) => {
        const tabType = getTabType(tab as TabType | { type: TabType; index: number });
        return tabType === activeTabType;
      });

      let updatedTabs;
      if (tabExists) {
        updatedTabs = existingTabs.map((tab) => {
          const tabType = getTabType(tab as TabType | { type: TabType; index: number });
          if (tabType === activeTabType) {
            return {
              ...tab,
              comingSoon: newValue,
            };
          }
          return tab;
        });
      } else {
        const usedIndicesSet = new Set(existingTabs.map((tab) => {
          const tabObj = tab as TabType | { type: TabType; index: number };
          return isTabObject(tabObj) ? tabObj.index : 0;
        }));
        let tabIndex = 0;
        while (usedIndicesSet.has(tabIndex)) {
          tabIndex++;
        }
        updatedTabs = [
          ...existingTabs,
          {
            type: activeTabType,
            index: tabIndex,
            comingSoon: newValue,
          },
        ];
      }

      const updatedEvent = await updateEvent({
        ...event,
        tabs: updatedTabs,
      });

      if (updatedEvent) {
        const reloadedEvent = await getEventById(event.id);
        if (reloadedEvent) {
          dispatch(NewEvent(reloadedEvent));
          toast.success("Coming soon status updated successfully");
        }
      }
    } catch (error) {
      toast.error("Error updating coming soon status");
      setComingSoonToggleByTab((prev) => ({
        ...prev,
        [activeTabType]: !newValue,
      }));
    }
  }, [event, dispatch, getTabType, isTabObject]);

  const tabsPages = useMemo(() => {
    return {
        registration: (
          <StaffRegistrationTab
            isEditMode={isRegistrationEditMode}
            onEditClick={handleRegistrationEditClick}
            onEventUpdate={(updatedEvent) => {
              if (updatedEvent) {
                dispatch(NewEvent(updatedEvent));
              }
            }}
          />
        ),
      accomodation: (
        <StaffAccomodationsTab
          event={event}
          isEditMode={isAccommodationsEditMode}
          onEditClick={handleAccommodationsEditClick}
          onEventUpdate={(updatedEvent) => {
            if (updatedEvent) {
              dispatch(NewEvent(updatedEvent));
            }
          }}
        />
      ),
      specialEvent: (
        <StaffSpecialEventTab
          event={event}
          isEditMode={isEditMode("specialEvent")}
          onEditClick={() => toggleEditMode("specialEvent")}
          onEventUpdate={(updatedEvent) => {
            if (updatedEvent) {
              dispatch(NewEvent(updatedEvent));
            }
          }}
        />
      ),
      overview: (
        <StaffOverviewTab
          event={event}
          isEditMode={isOverviewEditMode}
          onEditClick={handleOverviewEditClick}
          onEventUpdate={(updatedEvent) => {
            if (updatedEvent) {
              dispatch(NewEvent(updatedEvent));
            }
          }}
        />
      ),
      gallery: (
        <StaffPhotoGalleryTab
          event={event}
          isEditMode={isEditMode("gallery")}
          onEditClick={() => toggleEditMode("gallery")}
          onEventUpdate={(updatedEvent) => {
            if (updatedEvent) {
              dispatch(NewEvent(updatedEvent));
            }
          }}
        />
      ),
      contactUs: (
        <StaffContactUsTab
          event={event}
          isEditMode={isEditMode("contactUs")}
          onEditClick={() => toggleEditMode("contactUs")}
          onEventUpdate={(updatedEvent) => {
            if (updatedEvent) {
              dispatch(NewEvent(updatedEvent));
            }
          }}
        />
      ),
      program: (
        <StaffProgramTab
          event={event}
          isEditMode={isEditMode("program")}
          onEditClick={() => toggleEditMode("program")}
          onEventUpdate={(updatedEvent) => {
            if (updatedEvent) {
              dispatch(NewEvent(updatedEvent));
            }
          }}
        />
      ),
    } as Record<TabType, JSX.Element>;
  }, [event, isEditMode, toggleEditMode, isRegistrationEditMode, isAccommodationsEditMode, isOverviewEditMode, handleRegistrationEditClick, handleAccommodationsEditClick, handleOverviewEditClick, dispatch]);

  const allTabTypes = (Object.keys(TabEnum) as TabType[]).filter(
    (tabType) => tabType !== "attendees"
  );

  const eventTabIndices = new Map<TabType, number>();
  if (event?.tabs) {
    event.tabs.forEach((tab, idx) => {
      const tabType = getTabType(
        tab as TabType | { type: TabType; index: number }
      );
      const tabObj = tab as TabType | { type: TabType; index: number };
      const index = isTabObject(tabObj) ? tabObj.index : idx;
      eventTabIndices.set(tabType, index);
    });
  }

  const usedIndices = new Set(eventTabIndices.values());
  let nextAvailableIndex = 0;

  const processedTabs = allTabTypes
    .map((tabType: TabType) => {
      let index: number;
      if (eventTabIndices.has(tabType)) {
        index = eventTabIndices.get(tabType)!;
      } else {
        while (usedIndices.has(nextAvailableIndex)) {
          nextAvailableIndex++;
        }
        index = nextAvailableIndex;
        usedIndices.add(index);
        nextAvailableIndex++;
      }
      return { type: tabType, index };
    })
    .sort((a: { type: TabType; index: number }, b: { type: TabType; index: number }) => a.index - b.index);

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-start lg:justify-end items-center gap-2 mb-4">
        <LegacyButton
          text="ATTENDEES"
          content="textIcon"
          icon={
            <img src={chevronRightButton} alt="chevron" className="w-3 h-3" />
          }
          color="accent"
          className="w-full md:w-[180px]"
          onClick={() => navigate(`/events-staff/${id}/attendees`)}
        />
        <LegacyButton
          text="GENERAL SETTINGS"
          content="textIcon"
          icon={
            <img src={chevronRightButton} alt="chevron" className="w-3 h-3" />
          }
          color="accent"
          className="w-full md:w-[250px]"
          onClick={() => navigate(`/events-staff/edit/${id}`)}
        />
      </div>
      {processedTabs.length > 0 && (
        <div className="events-tabs-container">
          {processedTabs.map((tab: { type: TabType; index: number }, index: number) => {
            const tabType = tab.type;
            return (
              <button
                key={`${tabType}-${index}`}
                onClick={() => dispatch(setStaffSelectedTab(index))}
                className={
                  activeTab === index
                    ? "events-tab-button-active flex items-center gap-2"
                    : "events-tab-button flex items-center gap-2"
                }
              >
                <span>{TabEnum[tabType as keyof typeof TabEnum] || tabType}</span>
                {activeTab === index ? (
                  <ChevronDown
                    className={`fill-${
                      activeTab === index ? "white" : "gray-font"
                    }`}
                    style={{ width: "14px", height: "14px" }}
                  />
                ) : (
                  <ChevronDown
                    className={`fill-${
                      activeTab === index ? "white" : "gray-font"
                    }`}
                    rotation="rotate(-90deg)"
                    style={{ width: "14px", height: "14px" }}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
      {processedTabs.length > 0 && (() => {
        const tab = processedTabs[activeTab] || processedTabs[0];
        const activeTabType = tab ? tab.type : null;
        
        if (!activeTabType) {
          return null;
        }
        
        const baseConfig = tabEditConfig[activeTabType] || tabEditConfig.registration;
        let editModeValue = false;
        let config = baseConfig;
        
        if (activeTabType === "registration") {
          editModeValue = isRegistrationEditMode;
          config = {
            ...baseConfig,
            customAction: () => handleRegistrationEditClick(),
          };
        } else if (activeTabType === "accomodation") {
          editModeValue = isAccommodationsEditMode;
          config = {
            ...baseConfig,
            customAction: () => handleAccommodationsEditClick(),
          };
        } else if (activeTabType === "overview") {
          editModeValue = isOverviewEditMode;
          config = {
            ...baseConfig,
            customAction: () => handleOverviewEditClick(),
          };
        } else {
          editModeValue = isEditMode(activeTabType);
        }
        
        return (
          <TabEditButton
            config={config}
            isEditMode={editModeValue}
            onToggle={() => toggleEditMode(activeTabType)}
            eventTitle={event?.title}
          />
        );
      })()}
      {(event?.setupType === "webinar" || event?.setupType === "groupWebinar") && 
        processedTabs[activeTab]?.type === "registration" && (
        <div className="bg-white p-[20px] pb-0">
          <div className="bg-gray-25">
            <p className="text-gray-font text-base font-medium py-[16px] px-[20px]">
              This page won't be used on webinars.
            </p>
          </div>
        </div>
      )}
      {event?.tabs && event?.tabs.length > 0 && !event?.tabs.some(
        (tab: any) => tab.type === (processedTabs[activeTab]?.type as TabType)
        ) && <div className="bg-white p-[20px]">
        <div className="bg-gray-25">
          <p className="text-gray-font text-base font-medium py-[16px] px-[20px]">
            This tab is not live on the WSG site.
          </p>
        </div>
      </div>}
      {processedTabs.length > 0 && (
        <div>
          <div className="flex flex-row justify-between items-center w-full bg-white pt-[20px] px-[20px] pb-0">
            <div className="flex items-center gap-2.5">
              {(() => {
                const tab = processedTabs[activeTab] || processedTabs[0];
                const activeTabType = tab ? tab.type : null;
                
                if (!activeTabType) {
                  return null;
                }
                
                const currentToggleValue = comingSoonToggleByTab[activeTabType] || false;
                
                return (
                  <SwitchToggle
                    title="'Coming Soon' Mode on WSG Site"
                    titleFirst={true}
                    checked={currentToggleValue}
                    seter={(newValue: boolean) => handleComingSoonToggleChange(activeTabType, newValue)}
                    className="[&>button.bg-Default]:!bg-Default [&>button.bg-accentDefault]:!bg-Default"
                  />
                );
              })()}
            </div>
         </div>
        <div className="events-content-container">
          {(() => {
            const tab = processedTabs[activeTab] || processedTabs[0];
            const tabType = tab ? tab.type : null;
            return tabType ? tabsPages[tabType as TabType] : null;
          })()}
        </div>
        </div>
      )}
    </div>
  );
}
